import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiCallOut,
  EuiModalFooter,
  EuiButtonEmpty,
  EuiButton,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "../store/DataTableProvider";
import { useState } from "react";
import { captureException } from "@sentry/react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { useProjectTablesTableContext } from "components/ProjectTablesTable/provider/ProjectTablesTableProvider";
import { shallow } from "zustand/shallow";

interface ModalDeleteTablesProps {
  onClose: () => void;
}

export const ModalDeleteTables = ({ onClose }: ModalDeleteTablesProps) => {
  const visibleSelectedTables = useProjectTablesTableContext(
    (s) => s.getVisibleSelectedTables(),
    shallow,
  );
  const deleteTable = useDataTableContext((s) => s.deleteTable);

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    // Get the IDs of the selected tables
    const tableIds = visibleSelectedTables.map((table) => table.id);
    // track successes
    let successCount = 0;

    // try to delete all selected table
    for (const tableId of tableIds) {
      try {
        await deleteTable({ tableId });
        successCount++;
      } catch (err) {
        captureException(err);
      }
    }
    setIsLoading(false);

    // all tables deleted successfully
    if (successCount === tableIds.length) {
      addUtilityToastSuccess("All tables deleted successfully");
    }
    // not all but some tables deleted - show failure toast
    else if (successCount > 0) {
      addUtilityToastFailure(
        `${successCount} table(s) deleted successfully, but some failed`,
      );
    }
    // no tables deleted - show failure toast
    else {
      addUtilityToastFailure("Failed to delete tables");
    }

    // modal closed - tables will remain selected if not deleted (managed upstream)
    onClose();
  };
  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Confirm Deletion</EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiCallOut
          title="Proceed with caution!"
          color="warning"
          iconType="warning"
        >
          <p>
            Cells in the deleted table(s) may be referenced in other tables.
            Deleting this table might cause unwanted changes to your data.
          </p>
          <p>
            You are deleting:
            {visibleSelectedTables.map((table) => (
              <li key={table.id}>{table.name}</li>
            ))}
          </p>
        </EuiCallOut>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isLoading}
          fill
          color="danger"
          onClick={() => void handleSubmit()}
        >
          Delete
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
